import * as v from 'valibot';
import { dealerThemes } from '../../interfaces/settings';
import type { EditMaskConfig, Identifiers } from './definiton-mapping';

export const enum EditTypes {
    TEXT = 'text',
    TEXTAREA = 'textarea',
    RICH_TEXT = 'rte',
    IMAGE = 'image',
    IMAGE_TEXT = 'image-text',
    ABOUT_US_TEASER = 'about-us-teaser',
    EMPLOYEES = 'employees',
    TEXT_LIST = 'text-list',
    SELECT_TEXT_LIST = 'select-text-list',
    SELECT_CONTENT_LIST = 'select-content-list',
    SELECT_LIST = 'select-list',
    THEME = 'theme',
    SETTING = 'setting',
}

export const EDITABLE_CONTENTS = {
    home: {
        homeTeaser: EditTypes.SELECT_LIST,
        theme: EditTypes.THEME,
        imageTextContent: EditTypes.IMAGE_TEXT,
    },
    'about-us': {
        headline: EditTypes.TEXT,
        heroCopy: EditTypes.TEXTAREA,
        heroImage: EditTypes.IMAGE,
        aboutUsTeaser: EditTypes.ABOUT_US_TEASER,
        employees: EditTypes.EMPLOYEES,
        jobOfferBtnLink: EditTypes.TEXT,
        jobOfferHeroImage: EditTypes.IMAGE,
        jobOfferBenefits: EditTypes.TEXT_LIST,
    },
    businessCustomer: {
        bullet: EditTypes.SELECT_TEXT_LIST,
    },
    services: {
        serviceTextHeadline: EditTypes.TEXT,
        serviceText: EditTypes.TEXTAREA,
        serviceBullet: EditTypes.SELECT_TEXT_LIST,
        serviceHighlights: EditTypes.SELECT_CONTENT_LIST,
    },
    shop: {
        subText: EditTypes.TEXT,
    },
    imprint: {
        copy: EditTypes.RICH_TEXT,
    },
    privacy: {
        dataProtection: EditTypes.RICH_TEXT,
    },
    'dealer-campaign': {
        image: EditTypes.IMAGE,
        textImage: EditTypes.IMAGE,
        teaserImage: EditTypes.IMAGE,
        leasingOfferImage: EditTypes.IMAGE,
        cashPriceOfferImage: EditTypes.IMAGE,
        financeOfferImage: EditTypes.IMAGE,
    },
} as const;

export const EDIT_MASK_CONFIGURATIONS: Partial<Record<Identifiers, EditMaskConfig>> = {
    'home.theme': {
        headline: 'Farbmanagement der Startseite editieren',
        subHeadline:
            'Die Abbildungen zeigen beispielhaft wie Sie die Farbgebung der Startseite Ihrer Händlerwebsite anpassen können.',
        validatorHint: 'Optionen: Hyundai Sand, Hyundai Blau und Hyundai Blau mit weiß',
        validator: v.pipe(v.string(), v.picklist(dealerThemes)),
    },
    'home.homeTeaser': {
        headline: 'Themenkacheln editieren',
        subHeadline:
            'Mindestens 3 Kacheln müssen ausgewählt sein. Mit dem Ziehkreuz können Themen noch oben und unten verschoben werden.',
        validator: v.pipe(v.array(v.string()), v.minLength(3, 'Mindestens 3 Themenkacheln auswählen')),
    },
    'about-us.headline': {
        headline: 'Standort Text Überschrift ändern',
        subHeadline: 'Bitte geben Sie eine Überschrift ein.',
        validatorHint: 'Überschrift (maximal 45 Zeichen)',
        validator: v.pipe(
            v.string(),
            v.minLength(1, 'Die Überschrift muss mindestens ein Zeichen besitzen.'),
            v.maxLength(45, 'Maximale Länge von 45 Zeichen überschritten.'),
        ),
    },
    'about-us.heroCopy': {
        headline: 'Standort Text ändern',
        subHeadline: 'Bitte geben Sie einen Text ein.',
        validatorHint: 'Text (maximal 350 Zeichen)',
        validator: v.pipe(
            v.string(),
            v.minLength(1, 'Der Text muss mindestens ein Zeichen enthalten.'),
            v.maxLength(350, 'Maximale Länge von 350 Zeichen überschritten.'),
        ),
    },
    'about-us.jobOfferBtnLink': {
        headline: 'Link zu Stellenangebote',
        subHeadline:
            'Hier können Sie ein externes Karriereportal verlinken. Falls Sie hier keinen Link eintragen, öffnet sich automatisch für den Nutzer eine Mailvorlage an Ihre zentrale E-Mailadresse.',
        validator: v.union(
            [v.pipe(v.string(), v.empty()), v.pipe(v.string(), v.url())],
            'Bitte geben Sie eine gültige URL im folgenden Format ein https://www.hyundai.com/',
        ),
    },
    'about-us.jobOfferBenefits': {
        headline: 'Keyfacts für Stellenangebote editieren',
        subHeadline: 'Sie können die Keyfacts überschreiben.',
        validatorHint: 'Bulletpoints (maximal 70 Zeichen)',
        validator: v.array(v.pipe(v.string(), v.maxLength(70, 'Maximale Länge von 70 Zeichen überschritten.'))),
    },
    'about-us.employees': {
        headline: 'Ansprechpartner editieren',
        imageRequirements: {
            types: ['png', 'jpg'],
            maxFileSize: 1024 * 1024 * 5,
            maxWidth: 2000,
            minWidth: 640,
            minHeight: 360,
            aspectRatio: 'fedeadwa',
        },
    },
    'about-us.heroImage': {
        headline: 'Standortfoto bearbeiten',
        subHeadline:
            'Hier können Sie ein Bild Ihres Standorts einfügen. Falls Sie hier kein Bild einfügen, wird ein zentral gestelltes Bild eingefügt.',
    },
    'services.serviceBullet': {
        headline: 'Servicevorteile editieren',
        subHeadline:
            'Sie können maximal vier, mindestens drei auswählen. Wenn Sie keine spezifische Auswahl treffen, werden drei zentrale Elemente ausgegeben.',
        validator: v.pipe(
            v.array(v.string()),
            v.minLength(3, 'Mindestens 3 Vorteile auswählen.'),
            v.maxLength(4, 'Maximal 4 Vorteile auswählen.'),
        ),
    },
    'services.serviceHighlights': {
        headline: 'Servicehighlights ändern',
        subHeadline:
            'Keine Beschränkung in der maximalen Anzahl der Services. Es müssen jedoch mindestens 3 Services ausgewählt sein.',
        validator: v.pipe(v.array(v.string()), v.minLength(3, 'Mindestens 3 Services auswählen.')),
    },
    'services.serviceTextHeadline': {
        subHeadline: 'Bitte geben Sie eine Überschrift ein.',
        validator: v.pipe(
            v.string(),
            v.minLength(1, 'Der Text muss mindestens ein Zeichen besitzen.'),
            v.maxLength(45, 'Maximale Länge von 45 Zeichen überschritten.'),
        ),
    },
    'services.serviceText': {
        subHeadline: 'Bitte geben Sie einen Text ein.',
        validator: v.pipe(
            v.string(),
            v.minLength(1, 'Der Text muss mindestens ein Zeichen besitzen.'),
            v.maxLength(350, 'Maximale Länge von 350 Zeichen überschritten.'),
        ),
    },
    'privacy.dataProtection': {
        headline: 'Datenschutzbauftragter editieren',
    },
    'dealer-campaign.image': {
        headline: 'Kampagnen Bild',
        imageRequirements: {
            types: ['png', 'jpg'],
            maxFileSize: 1024 * 1024 * 5,
            maxWidth: 2000,
            minWidth: 640,
            minHeight: 360,
            aspectRatio: '16:9 (z. B. 1600 x 900 Pixel)',
        },
    },
    'businessCustomer.bullet': {
        headline: 'Gebewerbekundenvorteile editieren',
        subHeadline:
            'Sie können maximal drei Vorteile und mindestens zwei Vorteile auswählen. Wenn Sie keine spezifische Auswahl treffen, werden drei zentrale Elemente ausgegeben.',
        validator: v.pipe(
            v.array(v.string()),
            v.minLength(2, 'Mindestens 2 Vorteile auswählen.'),
            v.maxLength(3, 'Maximal 3 Vorteile auswählen.'),
        ),
    },
    'home.imageTextContent': {
        headline: 'Bild-Text Modul editieren',
        subHeadline: 'Bild und Überschrift sind Pflichtfelder. Der Rest ist optional.',
        imageRequirements: {
            types: ['png', 'jpg'],
            maxFileSize: 1024 * 1024 * 5,
            maxWidth: 2000,
            minWidth: 640,
            minHeight: 360,
            aspectRatio: '16:9 (z. B. 1600 x 900 Pixel)',
        },
    },
    'dealer-campaign.textImage': {
        headline: 'Kampagnen Textbild',
        imageRequirements: {
            types: ['png', 'jpg'],
            maxFileSize: 1024 * 1024 * 5,
            maxWidth: 2000,
            minWidth: 640,
            minHeight: 360,
            aspectRatio: '16:9 (z. B. 1600 x 900 Pixel)',
        },
    },
    'dealer-campaign.teaserImage': {
        headline: 'Kampagnen Teaserbild',
        imageRequirements: {
            types: ['png', 'jpg'],
            maxFileSize: 1024 * 1024 * 5,
            maxWidth: 2000,
            maxHeight: 2000,
            minWidth: 400,
            minHeight: 400,
            aspectRatio: '1:1 (z. B. 800 x 800 Pixel)',
        },
    },
    'dealer-campaign.leasingOfferImage': {
        headline: 'Kampagnen Leasingoffer Bild',
        imageRequirements: {
            types: ['png', 'jpg'],
            maxFileSize: 1024 * 1024 * 5,
            maxWidth: 2000,
            minWidth: 640,
            minHeight: 360,
            aspectRatio: '16:9 (z. B. 1600 x 900 Pixel)',
        },
    },
    'dealer-campaign.financeOfferImage': {
        headline: 'Kampagnen Finance Bild',
        imageRequirements: {
            types: ['png', 'jpg'],
            maxFileSize: 1024 * 1024 * 5,
            maxWidth: 2000,
            maxHeight: 2000,
            minWidth: 400,
            minHeight: 400,
            aspectRatio: '1:1 (z. B. 800 x 800 Pixel)',
        },
    },
    'dealer-campaign.cashPriceOfferImage': {
        headline: 'Kampagnen Barpreisangebot Bild',
        imageRequirements: {
            types: ['png', 'jpg'],
            maxFileSize: 1024 * 1024 * 5,
            maxWidth: 2000,
            minWidth: 640,
            minHeight: 360,
            aspectRatio: '16:9 (z. B. 1600 x 900 Pixel)',
        },
    },
    'imprint.copy': {
        subHeadline: 'Geben Sie das Impressum an.',
        validator: v.pipe(v.string(), v.minLength(8, 'Der Text muss mindestens 1 Zeichen enthalten.')),
    },
    'shop.subText': {
        subHeadline: 'Hier können Sie eine Unterüberschrift eingeben.',
    },
} as const;
